import type { ItemFieldRow } from './item-field-reader';
import { isPhpNumeric, phpJsonFloat } from './php-decimal-values';
import { phpJsonTruthy } from './php-json-values';
import { createCatalogDefaultProviderReader } from './provider-catalog-order';
import type { ProviderCatalogRows } from './provider-catalog-order';
import { createInventoryReadContext } from './inventory-read-context';
import type { InventoryReadContext } from './inventory-read-context';
import { createLayerValueReader } from './layer-value-reader';
import { createLayerEntriesReader } from './layer-entries-reader';
import { readNodeItemId, readOverrideEntryAbilityId } from './layer-cell-readers';

const arrayLike = (value: unknown): value is ItemFieldRow => value !== null && typeof value === 'object';
// JavaScript Array.length is not a PHP array entry.
const has = (value: unknown, key: string): boolean => arrayLike(value) && Object.prototype.propertyIsEnumerable.call(value, key);
const own = (value: unknown, key: string): unknown => has(value, key) ? (value as Readonly<Record<string, unknown>>)[key] : undefined;
const set = (row: Record<string, unknown>, key: string, value: unknown): void => {
    Object.defineProperty(row, key, { value, enumerable: true, writable: true, configurable: true });
};
function clone(value: unknown): unknown {
    if (!arrayLike(value)) return value ?? null;
    const entries = Object.entries(value);
    return entries.every(([key], i) => key === String(i)) ? entries.map(([, child]) => clone(child))
        : Object.fromEntries(entries.map(([key, child]) => [key, clone(child)]));
}
function writable(value: unknown, operation: 'offset' | 'append' = 'offset', key = ''): Record<string, unknown> {
    if (value == null || value === false) return {};
    if (!arrayLike(value)) {
        if (typeof value === 'string' && operation === 'offset' && !/^[\t\n\r\v\f ]*[+-]?\d+[\t\n\r\v\f ]*$/.test(key)) {
            throw new TypeError('Cannot access offset of type string on string');
        }
        throw new StatusArrayWriteError('Cannot append status data to a PHP scalar');
    }
    return Object.fromEntries(Object.entries(value));
}
export class StatusArrayWriteError extends Error {}
export class StatusArrayAppendOverflow extends StatusArrayWriteError {
    constructor() { super('Cannot add element to PHP array: next numeric key is occupied'); this.name = 'StatusArrayAppendOverflow'; }
}
/** JSON-decoded PHP array next index, including PHP 8.3+ negative integer keys. */
function append(array: ItemFieldRow, value: unknown, borrowed = false): ItemFieldRow {
    const out = Object.fromEntries(Object.entries(array));
    let maximum: bigint | undefined;
    for (const key of Object.keys(out)) if (/^(?:0|-?[1-9]\d*)$/.test(key)) {
        const integer = BigInt(key);
        if (integer < -9223372036854775808n || integer > 9223372036854775807n) continue;
        if (maximum === undefined || integer > maximum) maximum = integer;
    }
    if (maximum === 9223372036854775807n) throw new StatusArrayAppendOverflow();
    out[String(maximum === undefined ? 0n : maximum + 1n)] = value;
    if (!borrowed) return clone(out) as ItemFieldRow;
    const entries = Object.entries(out);
    return entries.every(([key], index) => key === String(index)) ? entries.map(([, child]) => child) : out;
}
/** Internal generic PHP append for other pure constructor stages. */
export const appendPhpCompileArray = append;
function strictEqual(left: unknown, right: unknown): boolean {
    if (!arrayLike(left) || !arrayLike(right)) return left === right;
    const a = Object.entries(left), b = Object.entries(right);
    return a.length === b.length && a.every(([key, value], i) => key === b[i]![0] && strictEqual(value, b[i]![1]));
}
const keep = new Set(['status_id', 'status_target_id', 'status_caster_id', 'status_connected_id']);
const emptyScalar = (value: unknown): boolean => value === null || value === '' || (isPhpNumeric(value) && phpJsonFloat(value) === 0);

/** ft_lean_status_entry. False is retained, identity/connection keys are never
 * elided, and only one zero-key wrapper is inspected (not recursively flattened).
 */
export function leanStatusEntry(entry: ItemFieldRow, borrowed = false): ItemFieldRow {
    if (!arrayLike(entry)) throw new TypeError('Status entry requires a PHP array');
    const out = Object.fromEntries(Object.entries(entry).filter(([key, value]) => {
        if (keep.has(key)) return true;
        if (emptyScalar(value)) return false;
        if (arrayLike(value)) {
            const keys = Object.keys(value);
            if (keys.length === 0 || (keys.length === 1 && has(value, '0') && emptyScalar(own(value, '0')))) return false;
        }
        return true;
    }));
    return borrowed ? out : clone(out) as ItemFieldRow;
}

/** ft_status_append as a returned copy for compiler use only. A duplicate ID
 * leaves the existing entry intact; this is not status_upsert or a DB writer.
 */
export function appendCompiledStatusRow(target: ItemFieldRow, mode: string, input: ItemFieldRow): ItemFieldRow {
    if (!arrayLike(target) || !arrayLike(input)) throw new TypeError('Status append requires PHP arrays');
    const out = writable(target);
    appendCompiledStatusRowInPlace(out, mode, input);
    return clone(out) as ItemFieldRow;
}
/** Internal producer helper: mutate only a caller-owned working root. */
export function appendCompiledStatusRowInPlace(target: Record<string, unknown>, mode: string, input: ItemFieldRow): void {
    if (!arrayLike(input)) throw new TypeError('Status append requires PHP arrays');
    const entry = writable(input);
    if (arrayLike(entry.item_type)) entry.item_type = own(entry.item_type, '0') ?? '';
    const lean = leanStatusEntry(entry, true), sid = own(lean, 'status_id') ?? null;
    const existing = own(own(target, 'statuses'), mode);
    if (sid !== null && arrayLike(existing)) {
        for (const row of Object.values(existing)) if (strictEqual(own(row, 'status_id') ?? null, sid)) return;
    }
    const statuses = writable(target.statuses, 'offset', mode);
    set(statuses, mode, append(writable(own(statuses, mode), 'append'), lean, true));
    target.statuses = statuses;
}

/** Actual layer_append destination rule: use an existing canonical slot, else
 * the legacy root. Rich entries, empty arrays and scalars promote to a pair.
 */
export function appendCompiledLayer(target: ItemFieldRow, name: string, value: unknown): ItemFieldRow {
    if (!arrayLike(target)) throw new TypeError('Layer append requires a PHP array');
    const out = writable(target);
    appendCompiledLayerInPlace(out, name, value);
    return clone(out) as ItemFieldRow;
}
/** Internal producer helper: retains the public helper's destination rule. */
export function appendCompiledLayerInPlace(out: Record<string, unknown>, name: string, value: unknown): void {
    const bucket = own(out, 'item_layer_types');
    const canonical = arrayLike(bucket) && has(bucket, name);
    const destination = canonical ? writable(bucket) : out;
    if (!canonical && !has(out, name)) set(destination, name, [value]);
    else {
        const existing = own(destination, name);
        set(destination, name, arrayLike(existing) && has(existing, '0') ? append(existing, value, true) : [existing, value]);
    }
    if (canonical) out.item_layer_types = destination;
}

export interface StatusOriginLayerReaders {
    /** Compiler-owned ancestors with immutable definition/list children. Public copy helpers omit this. */
    borrowed?: boolean;
    hasLayer: (row: ItemFieldRow, name: string) => boolean;
    layerEntries: (row: ItemFieldRow, name: string) => ItemFieldRow;
}
/** Origin abilities fill only absent effect buckets, in the PHP authoring order. */
export function mergeStatusOriginAbilityLayers(target: ItemFieldRow, origin: ItemFieldRow, readers: StatusOriginLayerReaders): ItemFieldRow {
    if (!arrayLike(target) || !arrayLike(origin)) throw new TypeError('Status origin merge requires PHP arrays');
    const out = writable(readers.borrowed ? target : clone(target));
    for (const name of ['ability', 'full_ability', 'quick_ability', 'attack_ability', 'free_ability',
        'reaction_ability', 'opportunity_attack_ability', 'passive_ability']) {
        if (readers.hasLayer(out, name)) continue;
        for (const entry of Object.values(readers.layerEntries(origin, name))) appendCompiledLayerInPlace(out, name, entry);
    }
    return readers.borrowed ? out : clone(out) as ItemFieldRow;
}

export interface StatusOriginLayerDependencies {
    catalogRows: ProviderCatalogRows;
    abilityTypes: readonly string[];
    aggregationStrategy: (name: string) => string;
    resolveCarrierRef: (id: string) => ItemFieldRow | null;
    resolveInventoryInstance: (id: string) => ItemFieldRow | null;
    /** Supply the request guard when composing stages; the context owns resolver tracing. */
    inventoryContext?: InventoryReadContext;
    trace?: (event: readonly unknown[]) => void;
}
export function createStatusLayerReadContext(deps: StatusOriginLayerDependencies): StatusOriginLayerReaders & {
    layerValue: (row: ItemFieldRow | null, name: string) => unknown;
    inventoryContext: InventoryReadContext;
} {
    const emit = (event: readonly unknown[]): void => { deps.trace?.(event); };
    const nodeItemId = (row: ItemFieldRow): string | null => { const id = readNodeItemId(row); emit(['node', id]); return id; };
    const provider = createCatalogDefaultProviderReader({ catalogRows: deps.catalogRows, nodeItemId,
        resolveCarrierRef: id => { emit(['reference', id]); return deps.resolveCarrierRef(id); } });
    const inventoryContext = deps.inventoryContext ?? createInventoryReadContext(id => { emit(['inventory', id]); return deps.resolveInventoryInstance(id); });
    const providers = function* (row: ItemFieldRow): Generator<ItemFieldRow> { emit(['providers']); yield* provider.providers(row); };
    const scalar = createLayerValueReader({ nodeItemId, inventoryContext, providers });
    const lists = createLayerEntriesReader({ nodeItemId, inventoryContext, providers, abilityTypes: deps.abilityTypes,
        aggregationStrategy: name => { const strategy = deps.aggregationStrategy(name); emit(['strategy', name, strategy]); return strategy; },
        overrideEntryAbilityId: entry => { const id = readOverrideEntryAbilityId(entry); emit(['override', id]); return id; } });
    return { borrowed: true, layerValue: scalar.read, layerEntries: lists.readBorrowed, inventoryContext,
        hasLayer: (row, name) => scalar.read(row, name) !== null || phpJsonTruthy(lists.readBorrowed(row, name)) };
}
export function createStatusOriginLayerCompiler(deps: StatusOriginLayerDependencies): (target: ItemFieldRow, origin: ItemFieldRow) => ItemFieldRow {
    const readers = createStatusLayerReadContext(deps);
    return (target, origin) => mergeStatusOriginAbilityLayers(target, origin, { ...readers, borrowed: false });
}
